/**
 * 多模态空间灯光：深度/法线离屏采样、坐标映射、遮挡与法线驱动的光斑形态建议。
 * 与 App.tsx 中 AnalysisCoordinateMeta 数值域一致。
 */

export type SpatialCoordinateMeta = {
  base64Width: number;
  base64Height: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  originalWidth: number;
  originalHeight: number;
};

export type SpatialSample = {
  /** 深度灰度归一化 0~1，越大通常越近/越亮（依工作流而定，仅作相对比较） */
  zNorm: number;
  /** 世界/切空间法线（单位向量近似） */
  nx: number;
  ny: number;
  nz: number;
  px: number;
  py: number;
};

export type SpatialMapsContext = {
  depthCanvas: HTMLCanvasElement;
  normalCanvas: HTMLCanvasElement;
  meta: SpatialCoordinateMeta;
};

/** 与 App mapModelNormalizedToOriginalPixel 同公式 */
export function modelNormalizedToOriginalPixel(
  modelX: number,
  modelY: number,
  meta: SpatialCoordinateMeta,
): { x: number; y: number } {
  const realX = (Number(modelX) / 1000) * meta.base64Width - meta.offsetX;
  const realY = (Number(modelY) / 1000) * meta.base64Height - meta.offsetY;
  const originalX = realX / Math.max(1e-6, meta.scale);
  const originalY = realY / Math.max(1e-6, meta.scale);
  return {
    x: Math.max(0, Math.min(meta.originalWidth - 1, Math.round(originalX))),
    y: Math.max(0, Math.min(meta.originalHeight - 1, Math.round(originalY))),
  };
}

function decodeNormalRgb(r: number, g: number, b: number): { nx: number; ny: number; nz: number } {
  const nx = (r / 255) * 2 - 1;
  const ny = (g / 255) * 2 - 1;
  const nz = (b / 255) * 2 - 1;
  const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
  return { nx: nx / len, ny: ny / len, nz: nz / len };
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('spatial map image load failed'));
    img.src = src;
  });
}

/**
 * 两个隐藏画布，尺寸与「原图层」像素一致，drawImage 拉伸填满以锁定与原图相同的宽高比域。
 */
export async function createOffscreenSpatialMaps(
  depthDataUrl: string,
  normalDataUrl: string,
  originalWidth: number,
  originalHeight: number,
  meta: SpatialCoordinateMeta,
): Promise<SpatialMapsContext> {
  const w = Math.max(1, Math.round(originalWidth));
  const h = Math.max(1, Math.round(originalHeight));
  const [depthImg, normalImg] = await Promise.all([loadImage(depthDataUrl), loadImage(normalDataUrl)]);

  const depthCanvas = document.createElement('canvas');
  const normalCanvas = document.createElement('canvas');
  depthCanvas.width = w;
  depthCanvas.height = h;
  normalCanvas.width = w;
  normalCanvas.height = h;
  Object.assign(depthCanvas.style, { position: 'fixed', left: '-9999px', top: '0', pointerEvents: 'none', opacity: '0' });
  Object.assign(normalCanvas.style, { position: 'fixed', left: '-9999px', top: '0', pointerEvents: 'none', opacity: '0' });

  const dctx = depthCanvas.getContext('2d', { willReadFrequently: true });
  const nctx = normalCanvas.getContext('2d', { willReadFrequently: true });
  if (!dctx || !nctx) throw new Error('Canvas 2D 不可用');

  dctx.drawImage(depthImg, 0, 0, w, h);
  nctx.drawImage(normalImg, 0, 0, w, h);

  return { depthCanvas, normalCanvas, meta };
}

/**
 * normX/normY：Gemini 0~1000；读取单点深度与法线。
 */
export function getSpatialData(
  ctx: SpatialMapsContext,
  normX: number,
  normY: number,
): SpatialSample {
  const { depthCanvas, normalCanvas, meta } = ctx;
  const { x: px, y: py } = modelNormalizedToOriginalPixel(normX, normY, meta);
  const dctx = depthCanvas.getContext('2d', { willReadFrequently: true });
  const nctx = normalCanvas.getContext('2d', { willReadFrequently: true });
  if (!dctx || !nctx) throw new Error('Canvas 2D 不可用');

  const dd = dctx.getImageData(px, py, 1, 1).data;
  const nd = nctx.getImageData(px, py, 1, 1).data;
  const lum = (dd[0] + dd[1] + dd[2]) / (3 * 255);
  const { nx, ny, nz } = decodeNormalRgb(nd[0], nd[1], nd[2]);
  return { zNorm: Math.max(0, Math.min(1, lum)), nx, ny, nz, px, py };
}

/** 邻域深度均值，用于遮挡：中心 Z 明显高于邻域 → 可能是灯点浮在物体前或被误判 */
export function computeOcclusionFactor(depthCanvas: HTMLCanvasElement, px: number, py: number, radius = 2): number {
  const ctx = depthCanvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return 1;
  const w = depthCanvas.width;
  const h = depthCanvas.height;
  const x0 = Math.max(0, px - radius);
  const y0 = Math.max(0, py - radius);
  const x1 = Math.min(w - 1, px + radius);
  const y1 = Math.min(h - 1, py + radius);
  const rw = x1 - x0 + 1;
  const rh = y1 - y0 + 1;
  const img = ctx.getImageData(x0, y0, rw, rh).data;
  let sum = 0;
  let n = 0;
  let centerLum = 0;
  for (let j = 0; j < rh; j++) {
    for (let i = 0; i < rw; i++) {
      const idx = (j * rw + i) * 4;
      const lum = (img[idx] + img[idx + 1] + img[idx + 2]) / (3 * 255);
      const gx = x0 + i;
      const gy = y0 + j;
      if (gx === px && gy === py) centerLum = lum;
      sum += lum;
      n++;
    }
  }
  const mean = n > 0 ? sum / n : centerLum;
  const diff = centerLum - mean;
  // 明显高于邻域：弱化（例如灯芯高光点）
  if (diff > 0.12) return Math.max(0.35, 1 - diff * 2.5);
  return 1;
}

export type SpatialFixtureHint = {
  intensityScale: number;
  beamProfile?: 'narrow' | 'medium' | 'wide' | 'oval' | 'asym_wall' | 'shadowless' | 'track';
  downlightContext?: string;
  /** 与 getSpatialData 一致，供前端几何蒙版与渲染传参 */
  nx?: number;
  ny?: number;
  nz?: number;
  zNorm?: number;
};

/**
 * 对识别结果做空间增强：遮挡衰减 + 法线驱动的光斑形态（射灯/筒灯）。
 */
export function processSpatialMapping(
  fixtures: Array<{ x: number; y: number; type?: string }>,
  spatial: SpatialMapsContext,
): SpatialFixtureHint[] {
  const { depthCanvas } = spatial;
  return fixtures.map((f) => {
    const sample = getSpatialData(spatial, f.x, f.y);
    let intensityScale = computeOcclusionFactor(depthCanvas, sample.px, sample.py);

    let beamProfile: SpatialFixtureHint['beamProfile'];
    let downlightContext: SpatialFixtureHint['downlightContext'];

    const t = (f.type || '').toLowerCase();
    const isSpotLike = t === 'spotlight' || t === 'downlight';
    if (isSpotLike) {
      const { nx, ny, nz } = sample;
      const horiz = Math.sqrt(nx * nx + nz * nz);
      // 法线更接近水平 → 墙面洗光（椭圆）
      if (horiz > 0.72 && Math.abs(ny) < 0.55) {
        beamProfile = 'oval';
        downlightContext = 'office_wall_wash';
      } else if (ny < -0.45 && nz > 0.35) {
        // 指向上方分量 → 地面/顶面散光（宽圆）
        beamProfile = 'wide';
        downlightContext = 'living_table';
      }
    }

    return {
      intensityScale,
      beamProfile,
      downlightContext,
      nx: sample.nx,
      ny: sample.ny,
      nz: sample.nz,
      zNorm: sample.zNorm,
    };
  });
}
